import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./TwoFactorAuth.css";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Config } from "../../../Utils/Config";
import ConstantData from "../../../Utils/Constant";
import { handlingCatchError } from "../../../Utils/ErrorHandling";
import { HubConnectionBuilder } from "@microsoft/signalr";
import { apiCommonTrigger } from "../../../Utils/apiServicehelper";

const TwofactorAuthentication = () => {
  let timeout;
  const [otp, setOtp] = useState(
    Array(ConstantData.otpSettings.totalOTPLength).fill("")
  ); // Assuming OTP is 6 characters
  const [error, setError] = useState(""); // To store error message
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false); // Loading state for resend button
  const [timer, setTimer] = useState(ConstantData.otpSettings.resendWaitingSec); // Timer state for 75 seconds
  const [canResend, setCanResend] = useState(false); // State to control the resend button
  const [resendMessage, setResendMessage] = useState(""); // State for resend message (OTP Sent)
  const { secureId, flag } = useParams();
  const navigate = useNavigate();

  // Start a countdown timer for resend OTP after 75 seconds
  useEffect(() => {
    if (!canResend && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval); // Cleanup interval on unmount or when timer reaches 0
    } else if (timer === 0) {
      setCanResend(true);
    }
  }, [timer, canResend]);

  // useEffect(() => {
  //   fetchData();
  // }, []);

  useEffect(() => {
    const UrlValue = `${Config.API_URL}/notificationHub`;
    const connection = new HubConnectionBuilder()
      .withUrl(UrlValue, {
        withCredentials: true, // Ensures credentials are sent (cookies, auth headers, etc.)
      })
      .build();

    connection
      .start()
      .then(() => console.log("Connected to SignalR on auth page"))
      .catch((err) => console.error("Error connecting to SignalR:", err));

    connection.on("DenyMessage", (sId) => {
      try {
        const secureIdList = sId?.newsession;
        if (secureIdList.includes(secureId)) {
          navigate("/access-denied");
          caches.open("session-cache").then((cache) => {
          cache.put("api_flag", new Response("true"));
        });
        }
      } catch (error) {
        console.log(error, "DENY STATE ERROR", secureId);
      }
    });

    connection.on("MailTriggered", (response) => {
      if (response) {
        caches.open("session-cache").then((cache) => {
          cache.put("api_flag", new Response("true"));
        });
      }
    });

    fetchData();

    // Clean up the connection when the component is unmounted
    return () => {
      clearTimeout(timeout);
      connection
        .stop()
        .catch((err) =>
          console.error("Error stopping SignalR connection:", err)
        );
    };
  }, []);

  const fetchData = async () => {
    // flag - 0 refers to status code - 202
    if (flag === "0") {
      await clearCache(secureId, navigate);
      const cache = await caches.open("session-cache");
      const cacheResponse = await cache.match("api_flag");
      if (cacheResponse) await cache.delete("api_flag");
    }

    if (flag === "1") {
      try {
        const cache = await caches.open("session-cache");
        await cache.put("api_flag", new Response("true"));
      } catch (err) {
        console.error("Error adding api_flag to cache:", err);
      }
    }

    verifySession(secureId);
    // Check if the flag is already set in Cache Storage
    // Set a timeout of 20 seconds before calling the API
     timeout = setTimeout(() => {
      try {
        caches.match("api_flag").then((response) => {
          if (response) {
            console.log("API call already made. Skipping API call.");
          } else {
            // Make the API call
            const payload = { secureId: secureId };
            apiCommonTrigger("post",`${Config.API_URL}/AuthToken/resend-otp`,payload)
           .then((res) => {
              // After the API call is completed, store the flag in Cache Storage
              caches.open("session-cache").then((cache) => {
                cache.put("api_flag", new Response("true"));
              });
            });
          }
        });
      } catch (error) {
        handlingCatchError(error);
        console.error("Error during API call:", error);
      }

      clearTimeout(timeout);
    }, ConstantData.otpSettings.resendWaitingSec * 1000); // Delay API call by 75 seconds
  };

  const verifySession = async (secureid) => {
    try {
     
      const res = await  apiCommonTrigger("get",`${Config.API_URL}/AuthToken/isvalidsession/`,[secureid]);
      if (res.status !== 200) {
        localStorage.clear();
        navigate("/signin");
      }
    } catch (error) {
      handlingCatchError(error);
      localStorage.clear();
      navigate("/signin");
    }
  };

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (/[^a-zA-Z0-9]/.test(value)) return; // Prevent non-alphanumeric characters

    const updatedOtp = [...otp];
    updatedOtp[index] = value;
    setOtp(updatedOtp);

    // Focus the next input field when current input is filled
    if (value && index < otp.length - 1) {
      document.getElementById(`otp-input-${index + 1}`).focus();
    }

    // Automatically call handleSubmit when all OTP fields are filled
    if (updatedOtp.every((digit) => digit !== "") && index === otp.length - 1) {
      handleSubmit(updatedOtp);
    }
  };

  const handleKeyDown = (e, index) => {
    // Handle Backspace to focus previous input if the current field is empty
    if (e.key === "Backspace" && otp[index] === "") {
      if (index > 0) {
        document.getElementById(`otp-input-${index - 1}`).focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault(); // Prevent the default paste action
    const pastedValue = e.clipboardData.getData("Text"); // Get the pasted text
    if (!pastedValue) return true;
    const alphanumericOnly = pastedValue.replace(/[^a-zA-Z0-9]/g, ""); // Remove any non-alphanumeric characters

    const remainingDigits = otp.filter((digit) => digit === "").length;

    if (remainingDigits > 0) {
      // Get the characters to be appended from the pasted value
      const charactersToAdd = alphanumericOnly.slice(0, remainingDigits);

      const updatedOtp = [...otp];
      let insertIndex = 0;

      // Find the first empty spot and append the characters
      for (let i = 0; i < otp.length; i++) {
        if (otp[i] === "") {
          updatedOtp[i] = charactersToAdd[insertIndex];
          insertIndex++;
          if (insertIndex === charactersToAdd.length) break; // Stop if we have appended all characters
        }
      }

      setOtp(updatedOtp);

      // Automatically call handleSubmit when all OTP fields are filled
      if (updatedOtp.every((digit) => digit !== "")) {
        handleSubmit(updatedOtp);
      }
    }
  };

  const handleSubmit = async (submitedOtp = null) => {
    submitedOtp = submitedOtp || otp;
    // Check if all OTP fields are filled (i.e., have exactly 6 characters)
    if (submitedOtp.some((digit) => digit === "")) {
      setError("Please enter a 6-character OTP.");
      return;
    }

    // Clear any previous error
    setError("");

    // If validation passes, submit OTP
    const enteredOtp = submitedOtp.join("");
    const payload = {
      secureid: secureId, // assuming secureId is available in your context
      otp: enteredOtp,
    };
    setLoading(true); // Start loading

    try {
      
      const res = await apiCommonTrigger("post",`${Config.API_URL}/AuthToken/verify-2fa`,payload);
      if (res.status === 200) {
        localStorage.removeItem("impersonated");
        localStorage.setItem("accessKey", res.data.token);
        const userType = res.data.usertype;
        localStorage.setItem("user", JSON.stringify(res.data));
        if (userType == "Administrator") {
          localStorage.setItem("isAdmin", true);
          navigate("/admin/user-management");
        } else {
          navigate("/market-search");
          localStorage.removeItem("isAdmin");
        }
      } else {
        handlingCatchError(res, navigate);
      }
    } catch (err) {
      window.globalThis.REACT_SNACK_OPEN = true;
      window.globalThis.REACT_SNACK_COLOR = "error";
      window.globalThis.REACT_SNACK_MESSAGE =
        err?.response?.data?.message ||
        err?.response?.data ||
        "Please Try Again";
      document.getElementById("enable-error-snack")?.click();
      handlingCatchError(err);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const resendOtp = async () => {
    setResendLoading(true);
    try {
      const payload = { secureId: secureId };
     
      const res = await  apiCommonTrigger("post",`${Config.API_URL}/AuthToken/resend-otp`,payload);
      if (res.status === 200) {
        // Change the resend button text to OTP Sent
        setResendMessage("OTP Sent");
        setTimeout(() => {
          setResendMessage("");
          setTimer(ConstantData.otpSettings.resendWaitingSec);
          setCanResend(false);
        }, 1000);
      } else {
        handlingCatchError(res, navigate);
      }
    } catch (err) {
      window.globalThis.REACT_SNACK_OPEN = true;
      window.globalThis.REACT_SNACK_COLOR = "error";
      window.globalThis.REACT_SNACK_MESSAGE =
        err?.response?.data?.message ||
        err?.response?.data ||
        "Please Try Again";
      document.getElementById("enable-error-snack").click();
      handlingCatchError(err);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="auth-container-body">
      <div className="auth-container">
        <h2>Enter One Time Password</h2>
        <p>
          Hi ! As part of the regular security protocol for RVPark IQ's
          products, please input the One Time Password (OTP) that will be sent
          to the email address associated with this account. Access your email
          and copy-and-paste or retype the password here:
        </p>

        <div className="otp-input-container">
          {otp.map((digit, index) => (
            <input
              key={index}
              type="text"
              id={`otp-input-${index}`}
              value={digit}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)} // Handle Backspace event
              onPaste={(e) => handlePaste(e, index)} // Handle paste event
              maxLength="1"
              className="otp-input"
              autoFocus={index === 0}
            />
          ))}
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="footer-btn">
          <button
            className="submit-btn"
            onClick={() => {
              handleSubmit();
            }}
            disabled={loading}
          >
            {loading ? <div class="loader"></div> : "Verify OTP"}
          </button>
        </div>

        <p className="otp-description">
          This OTP is valid for 10 minutes from when it is issued and can only
          be used one time. It is case sensitive!
        </p>

        <div className="resend-section">
          <p>
            If you did not receive the OTP within 1 minute, you can have one
            resent using the button below.
          </p>
          {resendMessage ? (
            <span className="sent-item-msg">
              {" "}
              <CheckCircleIcon style={{ color: "green", marginRight: "8px" }} />
              {resendMessage}
            </span>
          ) : canResend ? (
            <button
              className="resend-btn"
              onClick={resendOtp}
              disabled={resendLoading}
            >
              {resendLoading ? <div class="loader"></div> : "Resend OTP"}
            </button>
          ) : (
            <p className="resend-timer">
              Please wait {timer}s before resending...
            </p>
          )}
        </div>

        {/* Footer Section */}
        <div className="footer-section">
          <div className="footer-contact">
            <p>
              Having trouble? Email{" "}
              <a href={`mailto:${Config.supportEmail}`}>
                {Config.supportEmail}
              </a>
              {` or go to`}{" "}
              <a href="http://www.rvparkiq.com">www.rvparkiq.com</a> and
              activate a CHAT session now <br />
              or call {Config.PhoneNumber}.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwofactorAuthentication;
